import { FormControlLabel, Grid } from '@mui/material';

// Styles
import {
    Text,
    FormControlSwitch,
    Container,
} from './styles'
import { AntSwitch, CustomButtonV2, CustomTextField, CustomFormControl, CustomChipStatus } from '../../pages/styles';

import { useTranslation } from 'react-i18next'

// Permissions
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../Context/PermissionsContext';
import { SaveOutlined } from '@mui/icons-material';

export const PasswordPolicy = ({ data, errors, loading, handleChange, handleState, handlePasswordSecurity, handlePasswordUsername, handlePasswordDictionary, handlePasswordPolicy }) => {
    const { t } = useTranslation();
    const ability = useAbility(AbilityContext);

    return (
        <Container>
			<Grid container spacing={2} alignItems="center">
				<Grid item xs={12}>
                    <FormControlLabel
                        disabled={loading}
                        sx={{ pl: 2 }}
                        control={
                            <AntSwitch
                                checked={data.isActive}
                                onChange={() => handleState(!data.isActive)}
                                inputProps={{ 'aria-label': 'controlled' }}
                            />
                        }
                        label={
							<CustomChipStatus
								enabled={data.isActive}
								label={data.isActive ? t('system.passwordPolicy.active') : t('system.passwordPolicy.inactive')}
								sx={{ ml: 1 }}
							/>
                        } 
					/>
				</Grid>

				<Grid item xs={12}></Grid>

				<Grid item xs={12} sm={6} md={3} xl={2}>
					<CustomTextField
						label={t('system.passwordPolicy.minimumDays')}
						type={'number'}
						value={data.minimumDay}
						onChange={e => handleChange(e, 'minimumDay')}
						onInput={(e) => {
							e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 3)
						}}
						disabled={loading || !data.isActive}
						error={errors.minimumDay.hasError}
						helperText={errors.minimumDay.message}
						sx={{ width: '100%' }}
					/>
				</Grid>

				<Grid item xs={12} sm={6} md={3} xl={2}>
					<CustomTextField
						label={t('system.passwordPolicy.maximumDays')}
						type={'number'}
						value={data.maximumDay}
						onChange={e => handleChange(e, 'maximumDay')}
						onInput={(e) => {
							e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 3)
						}}
						disabled={loading || !data.isActive}
						error={errors.maximumDay.hasError}
						helperText={errors.maximumDay.message}
						sx={{ width: '100%' }}
					/>
				</Grid>

				<Grid item xs={12} sm={6} md={3} xl={2}>
					<CustomTextField
						label={t('system.passwordPolicy.passwordCharacterLimits')}
						type={'number'}
						value={data.passwordCharacterLimit}
						onChange={e => handleChange(e, 'passwordCharacterLimit')}
						onInput={(e) => {
							e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 3)
						}}
						disabled={loading || !data.isActive}
						error={errors.passwordCharacterLimit.hasError}
						helperText={errors.passwordCharacterLimit.message}
						sx={{ width: '100%' }}
					/>
				</Grid>

				<Grid item xs={12} sm={6} md={3} xl={2}>
					<CustomTextField
						label={t('system.passwordPolicy.passwordHistory')}
						type={'number'}
						value={data.passwordHistory}
						onChange={e => handleChange(e, 'passwordHistory')}
						onInput={(e) => {
							e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 3)
						}}
						disabled={loading || !data.isActive}
						error={errors.passwordHistory.hasError}
						helperText={errors.passwordHistory.message}
						sx={{ width: '100%' }}
					/>
				</Grid>

				<Grid item xs={4}></Grid>

				<Grid item xs={12}>
					<FormControlSwitch
						disabled={loading || !data.isActive}
						sx={{ pl: 2 }}
						control={
							<AntSwitch
								checked={data.passwordUsername}
								onChange={() => handlePasswordUsername(!data.passwordUsername)}
								inputProps={{ 'aria-label': 'controlled' }}
							/>
						}
						label={
							<CustomChipStatus
								enabled={data.passwordUsername}
								label={data.passwordUsername
									? t('system.passwordPolicy.passwordUsernameNoApply')
									: t('system.passwordPolicy.passwordUsernameApply')
								}
								sx={{ ml: 1 }}
							/>
						} 
					/>
				</Grid>

				<Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
					<FormControlSwitch
						sx={{ pl: 2 }}
						disabled={loading || !data.isActive}
						control={
							<AntSwitch
								checked={data.passwordDictionary}
								onChange={() => handlePasswordDictionary(!data.passwordDictionary)}
								inputProps={{ 'aria-label': 'controlled' }}
							/>
						}
						label={
							<CustomChipStatus
								enabled={data.passwordDictionary}
								label={data.passwordDictionary
									? t('system.passwordPolicy.passwordDictionaryApply')
									: t('system.passwordPolicy.passwordDictionaryNoApply')
								}
								sx={{ ml: 1 }}
							/>
						} 
					/>
				</Grid>
				
				<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
					<CustomTextField
						label={t('system.passwordPolicy.dictionaryPath')}
						value={data.dictionaryPath}
						onChange={e => handleChange(e, 'dictionaryPath')}
						disabled={loading || !data.passwordDictionary || !data.isActive}
						error={errors.dictionaryPath.hasError}
						helperText={errors.dictionaryPath.message}
						sx={{ width: '100%' }}
					/>
				</Grid>

				<Grid item lg={4} xl={5} sx={{ display: {xs: 'none', lg: 'block' } }}></Grid>

				<Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
					<FormControlSwitch
						sx={{ pl: 2 }}
						disabled={loading || !data.isActive}
						control={
							<AntSwitch
								checked={data.passwordSecurity}
								onChange={() => handlePasswordSecurity(!data.passwordSecurity)}
								inputProps={{ 'aria-label': 'controlled' }}
							/>
						}
						label={
							<CustomChipStatus
								enabled={data.passwordSecurity}
								label={data.passwordSecurity
									? t('system.passwordPolicy.applyRule')
									: t('system.passwordPolicy.noApplyRule')
								}
								sx={{ ml: 1 }}
							/>
						} />
				</Grid>
				
				<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
					<CustomTextField
						label={t('system.passwordPolicy.securityRule')}
						value={data.passwordRule}
						onChange={e => handleChange(e, 'passwordRule')}
						disabled={loading || !data.passwordSecurity || !data.isActive}
						error={errors.passwordRule.hasError}
						helperText={errors.passwordRule.message}
            sx={{ width: '100%' }}
					/>
				</Grid>
				
				<Grid item xs={12}>
					<CustomFormControl>{t('system.passwordPolicy.validationMessage')}</CustomFormControl>
					<CustomTextField
						multiline
						fullWidth
						rows={4}
						value={data.passwordComments}
						onChange={e => handleChange(e, 'passwordComments')}
						disabled={loading || !data.passwordSecurity || !data.isActive}
						error={errors.passwordComments.hasError}
						helperText={errors.passwordComments.message}
						sx={{ width: '100%' }}
					/>
				</Grid>
				
				{ability.can("UpdatePasswordPolicy") &&
				<>
					<Grid item xs={8} sm={10} xl={11}></Grid>
					<Grid item xs={4} sm={2} xl={1} direction='row' justifyContent="flex-end">
						<CustomButtonV2
							btnType="primary"
							onClick={() => handlePasswordPolicy()}
							disabled={loading}
							startIcon={<SaveOutlined/>}
						>
							{t('common.save')}
						</CustomButtonV2>
					</Grid>
				</>
				}
				
				<Grid item xl={12}>
					{errors.general.hasError &&
						<Grid item xs={8}>
							<Text>{errors.general.message}</Text>
						</Grid>
					}
				</Grid>
			</Grid>
        </Container>
    )
}